import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import { isNewAifcExperienceEnabledNoExposure } from '../../ui/utils/new-aifc-experience-enabled';

import type { AIContentPositionEntry, SessionContextPluginState } from './types';

/**
 * CSS class applied to the inline decoration that shades an undone
 * Post Stream Review segment. Styled in `GlobalWrapper` (global editor styles)
 * with `token('color.background.neutral')`, mirroring how the other AI editor
 * decoration classes (e.g. `ai-selection-highlight`) are styled there.
 */
export const UNDONE_SEGMENT_DECORATION_CLASS = 'ai-undone-segment-highlight';

/**
 * The working set the Post Stream Review toolbar navigates:
 * `streamReviewSegments` (the FINE segments) in step-through mode, and
 * `aiContentPositions` (the coarse commit ranges) in aggregate mode. Mirrors how
 * the PostStreamReview screen resolves which array is authoritative for the
 * current mode. We iterate this top-level array directly and never descend into
 * `diffSegments` — in step-through mode `streamReviewSegments` are already the
 * flattened fine segments, so descending would double-paint the same range.
 */
const getWorkingSet = (
	pluginState: SessionContextPluginState,
): AIContentPositionEntry[] | undefined => {
	// Only prefer the fine `streamReviewSegments` in step-through mode, matching
	// the PostStreamReview screen's authoritative resolution. Gating on the mode
	// (not just non-emptiness) avoids shading the wrong positions if
	// `streamReviewSegments` is ever left populated during a mode transition.
	if (
		pluginState.currentStreamReviewMode === 'stepThrough' &&
		pluginState.streamReviewSegments &&
		pluginState.streamReviewSegments.length > 0
	) {
		return pluginState.streamReviewSegments;
	}
	return pluginState.aiContentPositions;
};

/**
 * A segment is "undone" when its applied slice is the original
 * pre-AI content — `appliedIndex === 0` (the field that replaced the old
 * `isUndone` boolean). Accepted segments leave the working set, so they never
 * qualify. Zero-width ranges (a fully-collapsed undone `add`) are skipped: there
 * is nothing to shade and ProseMirror rejects an empty inline decoration.
 */
const isUndoneSegment = (entry: AIContentPositionEntry): boolean =>
	entry.appliedIndex === 0 && !entry.isAccepted && entry.endPos > entry.startPos;

/**
 * Builds the inline gray-background decoration set for every undone
 * segment in the current working set.
 *
 * Returns `DecorationSet.empty` unless the New AIFC Editor Experience is enabled
 * (no-exposure check — this runs in the render path) and the session is actively
 * in Post Stream Review, so the decoration is torn down for free when the review
 * closes (status leaves `postStreamReview`, e.g. on `RESET`).
 *
 * Positions are read straight from plugin state, which `apply()` already remaps
 * on every doc change, so the ranges are always current. Out-of-bounds ranges
 * are defended against `doc.content.size` so a transient mid-transaction state
 * can never throw.
 */
export const buildUndoneSegmentDecorationSet = (
	doc: PMNode,
	pluginState: SessionContextPluginState,
): DecorationSet => {
	if (pluginState.status !== 'postStreamReview') {
		return DecorationSet.empty;
	}

	if (!isNewAifcExperienceEnabledNoExposure()) {
		return DecorationSet.empty;
	}

	const workingSet = getWorkingSet(pluginState);
	if (!workingSet || workingSet.length === 0) {
		return DecorationSet.empty;
	}

	const docSize = doc.content.size;
	const decorations: Decoration[] = [];

	workingSet.forEach((entry) => {
		if (!isUndoneSegment(entry)) {
			return;
		}
		const from = entry.startPos;
		const to = entry.endPos;
		// Defend against a range that a transient state left outside the current
		// document — an inline decoration outside `[0, docSize]` throws.
		if (from < 0 || to > docSize || from >= to) {
			return;
		}
		decorations.push(
			Decoration.inline(
				from,
				to,
				{ class: UNDONE_SEGMENT_DECORATION_CLASS, 'data-testid': UNDONE_SEGMENT_DECORATION_CLASS },
				// Key on the (non-overlapping) range, not the array index: accepting a
				// segment removes it from the front of the working set and would shift
				// every later index, making ProseMirror destroy/recreate all surviving
				// decorations. A position-based key keeps their identity stable.
				{ key: `${UNDONE_SEGMENT_DECORATION_CLASS}-${from}-${to}` },
			),
		);
	});

	if (decorations.length === 0) {
		return DecorationSet.empty;
	}

	return DecorationSet.create(doc, decorations);
};

/**
 * Creates a memoised decoration provider for the `ai-session-context`
 * plugin's `props.decorations`.
 *
 * `props.decorations(state)` is called by the view on EVERY `updateState` — not
 * just doc changes — so a naive rebuild would allocate a fresh `DecorationSet`
 * (and defeat ProseMirror's decoration diffing) on pure navigation transactions
 * that don't change the doc. We memoise on the working-set array reference plus
 * `status`:
 *   - Navigation with no doc change (a plain `POST_STREAM_REVIEW` re-open) keeps
 *     the same working-set array reference, so the cached set is returned
 *     untouched — nothing is recreated.
 *   - Undo / redo / accept and doc-change remaps all produce a NEW working-set
 *     array reference in `apply()`/reducer, so the set is rebuilt at the fresh
 *     positions exactly when — and only when — it needs to be.
 *
 * The memo is per-plugin-instance (closure), so it never leaks across editors.
 */
export const createUndoneSegmentDecorationProvider = (): ((
	doc: PMNode,
	pluginState: SessionContextPluginState,
) => DecorationSet) => {
	let cachedWorkingSet: AIContentPositionEntry[] | undefined;
	let cachedStatus: SessionContextPluginState['status'];
	let cachedDoc: PMNode | undefined;
	let cachedSet: DecorationSet = DecorationSet.empty;

	return (doc: PMNode, pluginState: SessionContextPluginState): DecorationSet => {
		const workingSet = getWorkingSet(pluginState);
		// The working-set reference and status fully determine the decoration set,
		// but the doc identity must match too: a doc change produces a new working
		// set via the remap in `apply()`, yet we still guard on `doc` so a
		// decoration set is never returned against a stale document.
		if (
			workingSet === cachedWorkingSet &&
			pluginState.status === cachedStatus &&
			doc === cachedDoc
		) {
			return cachedSet;
		}

		cachedSet = buildUndoneSegmentDecorationSet(doc, pluginState);
		cachedWorkingSet = workingSet;
		cachedStatus = pluginState.status;
		cachedDoc = doc;
		return cachedSet;
	};
};
